import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Rocket, CheckCircle2, AlertTriangle, XCircle, Clock, Edit3,
  Save, X, RefreshCw, Loader2, CalendarDays,
} from "lucide-react";
import { API } from "../App";

// -----------------------------------------------------------------------------
// Dispatch Readiness Dashboard — 6 hardcoded gates traffic-lit for the
// end-May 2026 print-dispatch target. JSON-config-backed on the server.
// Admin-only editing.
// -----------------------------------------------------------------------------

const STATUS_META = {
  green: {
    label: "On track", icon: CheckCircle2,
    card: "bg-emerald-500/10 border-emerald-500/40",
    text: "text-emerald-300", dot: "bg-emerald-400",
  },
  yellow: {
    label: "At risk", icon: AlertTriangle,
    card: "bg-amber-500/10 border-amber-500/40",
    text: "text-amber-300", dot: "bg-amber-400",
  },
  red: {
    label: "Not started / blocked", icon: XCircle,
    card: "bg-rose-500/10 border-rose-500/40",
    text: "text-rose-300", dot: "bg-rose-400",
  },
};

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
    });
  } catch (_) {
    return s;
  }
}

function fmtTimestamp(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString(undefined, {
      month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch (_) {
    return s;
  }
}

function daysUntil(s) {
  if (!s) return null;
  const target = new Date(s);
  if (Number.isNaN(target.getTime())) return null;
  const now = new Date();
  const ms = target.setHours(0, 0, 0, 0) - now.setHours(0, 0, 0, 0);
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

const DispatchReadinessPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/dispatch-readiness`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const daysToDispatch = useMemo(
    () => (data?.print_dispatch_target ? daysUntil(data.print_dispatch_target) : null),
    [data]
  );

  const openEdit = (gate) => {
    setEditingId(gate.id);
    setDraft({
      status: gate.status,
      eta: gate.eta || "",
      owner: gate.owner || "",
      notes_en: gate.notes_en || "",
      notes_pt_br: gate.notes_pt_br || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/dispatch-readiness/${editingId}`,
        draft,
        { withCredentials: true }
      );
      setData(res.data);
      setEditingId(null);
      setDraft({});
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) setError("Admin access required to update gates.");
      else setError(err.response?.data?.detail || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00C8E0] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="dispatch-readiness-page">
      <Header data={data} daysToDispatch={daysToDispatch} onRefresh={fetchData} />

      {error && (
        <div className="max-w-4xl mx-auto px-6 pt-4">
          <div className="bg-rose-900/10 border border-rose-800/30 rounded-lg p-3 text-rose-300 text-sm flex items-center justify-between"
               data-testid="dr-error-banner">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
            <button onClick={() => setError(null)} className="text-xs hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-3" data-testid="dr-gate-list">
          {(data?.gates || []).map((gate) => (
            <GateRow
              key={gate.id}
              gate={gate}
              editing={editingId === gate.id}
              draft={draft}
              setDraft={setDraft}
              onEdit={() => openEdit(gate)}
              onCancel={cancelEdit}
              onSave={saveEdit}
              saving={saving}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

const Header = ({ data, daysToDispatch, onRefresh }) => {
  const summary = data?.summary || { green: 0, yellow: 0, red: 0, total: 0 };
  return (
    <header className="border-b border-[#1E3A5F]/50 px-6 py-5 bg-[#0A1628]/80 backdrop-blur sticky top-0 z-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[#1A6BCC] to-[#00C8E0] flex items-center justify-center">
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold" data-testid="dr-title">Print-Dispatch Readiness</h1>
              <p className="text-xs text-slate-400">Brain Drain Series III · end-May 2026 target</p>
            </div>
          </div>
          <button
            onClick={onRefresh}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white border border-[#1E3A5F] rounded px-3 py-1.5 hover:border-[#1A6BCC]/50 transition-colors"
            data-testid="dr-refresh-btn"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryStat label="Target" value={fmtDate(data?.print_dispatch_target)} icon={CalendarDays} />
          <SummaryStat
            label="Days remaining"
            value={daysToDispatch !== null ? `${daysToDispatch}` : "—"}
            accent={daysToDispatch !== null && daysToDispatch < 14 ? "rose"
                     : daysToDispatch !== null && daysToDispatch < 30 ? "amber" : "emerald"}
            icon={Clock}
          />
          <SummaryStat
            label="Green"
            value={`${summary.green} / ${summary.total}`}
            accent="emerald"
            testid="dr-summary-green"
          />
          <SummaryStat
            label="Blocked"
            value={`${summary.red}`}
            accent={summary.red > 0 ? "rose" : "emerald"}
            testid="dr-summary-red"
          />
        </div>
      </div>
    </header>
  );
};

const ACCENT = {
  emerald: "text-emerald-300", amber: "text-amber-300", rose: "text-rose-300",
  default: "text-white",
};

const SummaryStat = ({ label, value, accent = "default", icon: Icon, testid }) => (
  <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] text-slate-500 uppercase tracking-wider mb-1">
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </div>
    <div className={`text-lg font-bold font-mono ${ACCENT[accent] || ACCENT.default}`}>{value}</div>
  </div>
);

const GateRow = ({ gate, editing, draft, setDraft, onEdit, onCancel, onSave, saving }) => {
  const status = STATUS_META[gate.status] || STATUS_META.red;
  const StatusIcon = status.icon;
  return (
    <div
      className={`rounded-lg border p-4 transition-colors ${status.card}`}
      data-testid={`dr-gate-${gate.id}`}
    >
      <div className="flex items-start gap-3">
        <StatusIcon className={`w-5 h-5 ${status.text} flex-shrink-0 mt-0.5`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h3 className="text-sm font-bold text-white">{gate.name_en}</h3>
              <p className="text-[11px] text-slate-400 mt-0.5" lang="pt-BR">{gate.name_pt_br}</p>
            </div>
            {!editing && (
              <button
                onClick={onEdit}
                className="text-[10px] text-slate-400 hover:text-white flex items-center gap-1 border border-[#1E3A5F] rounded px-2 py-1 hover:border-[#1A6BCC]/50 transition-colors"
                data-testid={`dr-edit-${gate.id}`}
              >
                <Edit3 className="w-3 h-3" /> Edit
              </button>
            )}
          </div>

          {editing ? (
            <EditForm draft={draft} setDraft={setDraft} onSave={onSave} onCancel={onCancel} saving={saving} gateId={gate.id} />
          ) : (
            <GateDetails gate={gate} status={status} />
          )}
        </div>
      </div>
    </div>
  );
};

const GateDetails = ({ gate, status }) => (
  <div className="mt-2 space-y-2">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
      <FieldDetail label="Status" value={
        <span className={`${status.text} font-bold`}>{status.label}</span>
      } />
      <FieldDetail label="Owner" value={gate.owner || "—"} />
      <FieldDetail label="ETA" value={fmtDate(gate.eta)} />
      <FieldDetail label="Last checked" value={fmtTimestamp(gate.last_checked)} />
    </div>
    {gate.notes_en && (
      <div className="text-xs text-slate-300 leading-relaxed pt-1 border-t border-[#1E3A5F]/30"
           data-testid={`dr-notes-${gate.id}`}>
        {gate.notes_en}
      </div>
    )}
  </div>
);

const FieldDetail = ({ label, value }) => (
  <div>
    <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
    <div className="text-xs text-slate-200">{value}</div>
  </div>
);

const EditForm = ({ draft, setDraft, onSave, onCancel, saving, gateId }) => (
  <div className="mt-3 space-y-3" data-testid={`dr-edit-form-${gateId}`}>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Status</label>
        <div className="flex gap-1">
          {["green", "yellow", "red"].map((s) => {
            const meta = STATUS_META[s];
            const active = draft.status === s;
            return (
              <button
                key={s}
                onClick={() => setDraft({ ...draft, status: s })}
                className={`flex-1 px-2 py-1.5 rounded text-xs border transition-colors flex items-center justify-center gap-1 ${
                  active
                    ? `${meta.card} ${meta.text} font-bold`
                    : "border-[#1E3A5F] text-slate-500 hover:border-[#1A6BCC]/40"
                }`}
                data-testid={`dr-status-${s}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                {s}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">ETA</label>
        <input
          type="date"
          value={draft.eta || ""}
          onChange={(e) => setDraft({ ...draft, eta: e.target.value })}
          className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-1.5 text-xs text-white focus:border-[#1A6BCC] focus:outline-none font-mono"
          data-testid="dr-edit-eta"
        />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Owner</label>
        <input
          type="text"
          value={draft.owner || ""}
          onChange={(e) => setDraft({ ...draft, owner: e.target.value })}
          className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-1.5 text-xs text-white focus:border-[#1A6BCC] focus:outline-none"
          data-testid="dr-edit-owner"
        />
      </div>
    </div>
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Notes (EN)</label>
      <textarea
        value={draft.notes_en || ""}
        onChange={(e) => setDraft({ ...draft, notes_en: e.target.value })}
        rows={2}
        className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-1.5 text-xs text-white focus:border-[#1A6BCC] focus:outline-none resize-none"
        data-testid="dr-edit-notes-en"
      />
    </div>
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Notas (PT-BR)</label>
      <textarea
        value={draft.notes_pt_br || ""}
        onChange={(e) => setDraft({ ...draft, notes_pt_br: e.target.value })}
        rows={2}
        className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-1.5 text-xs text-white focus:border-[#1A6BCC] focus:outline-none resize-none"
        data-testid="dr-edit-notes-ptbr"
      />
    </div>
    <div className="flex gap-2 justify-end pt-1">
      <button
        onClick={onCancel}
        disabled={saving}
        className="text-xs px-3 py-1.5 rounded border border-[#1E3A5F] text-slate-400 hover:text-white hover:border-[#1A6BCC]/40 disabled:opacity-40 flex items-center gap-1.5"
        data-testid="dr-edit-cancel"
      >
        <X className="w-3 h-3" /> Cancel
      </button>
      <button
        onClick={onSave}
        disabled={saving}
        className="text-xs px-3 py-1.5 rounded bg-[#1A6BCC] hover:bg-[#1A6BCC]/80 disabled:opacity-40 text-white font-bold flex items-center gap-1.5"
        data-testid="dr-edit-save"
      >
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  </div>
);

export default DispatchReadinessPage;
